import React, { useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Grid,
  TextField,
  FormControl,
  FormHelperText,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Box,
  Typography,
  Chip,
  CircularProgress
} from '@mui/material';
import {
  PowerSettingsNewOutlined,
  TableChartOutlined,
  BoltOutlined
} from '@mui/icons-material';
import { useAutomationContext } from '../context/AutomationContext';
import { useAutomationMetadata } from '../hooks/useAutomationMetadata';
import { useValidation } from '../hooks/useValidation';
import { SectionCard } from '../components/StepSection';
import {
  inputSx,
  multilineInputSx,
  selectSx,
  placeholderSx,
  statusChipSx,
  tintSx,
  FONT
} from '../styles/wizardSx';

// `extraTriggers` ([{ id, name }]) adds events the object's metadata does not
// list: the flow builder's "On a schedule".
function TriggerStep({ extraTriggers }) {
  const { state, actions } = useAutomationContext();
  const { metadata, isLoading } = useAutomationMetadata();
  const { errors, getFieldError } = useValidation();

  const availableObjects = useMemo(() => {
    if (!metadata || !metadata.objects) {
      return [];
    }

    return Object.values(metadata.objects).sort((a, b) => {
      if (a.type !== b.type) {
        return a.type === 'standard' ? -1 : 1;
      }
      return a.name.localeCompare(b.name);
    });
  }, [metadata?.objects]);

  const availableTriggers = useMemo(() => {
    if (!state.wizardData.object || !metadata?.objects) return [];

    const objectMetadata = metadata.objects[state.wizardData.object];
    if (!objectMetadata) return [];

    return (objectMetadata.capabilities?.triggers || []).map(trigger => ({
      id: trigger,
      name: trigger.charAt(0).toUpperCase() + trigger.slice(1).replace('_', ' ')
    })).concat(extraTriggers || []);
  }, [state.wizardData.object, metadata?.objects, extraTriggers]);

  const handleFieldChange = (field, value) => {
    actions.updateWizardData({ [field]: value });
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress color="info" />
      </Box>
    );
  }

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <SectionCard title="Details" helper="Shown in the automation list and in the run logs.">
          <Grid container spacing={2.5}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Automation Name"
                required
                value={state.wizardData.name || ''}
                onChange={(e) => handleFieldChange('name', e.target.value)}
                error={!!getFieldError('name')}
                helperText={getFieldError('name')}
                placeholder="Enter a descriptive name for this automation"
                sx={inputSx}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                value={state.wizardData.description || ''}
                onChange={(e) => handleFieldChange('description', e.target.value)}
                placeholder="Describe what this automation does"
                multiline
                rows={3}
                sx={multilineInputSx}
              />
            </Grid>
          </Grid>
        </SectionCard>
      </Grid>

      <Grid item xs={12}>
        <SectionCard
          icon={<BoltOutlined fontSize="small" />}
          title="Trigger"
          helper="The record type to watch and the event that starts the automation."
        >
          <Grid container spacing={2.5}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth error={!!getFieldError('object')}>
                <InputLabel>Object *</InputLabel>
                <Select
                  value={state.wizardData.object || ''}
                  onChange={(e) => handleFieldChange('object', e.target.value)}
                  label="Object *"
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: 48 * 6 + 8, // 6 items * 48px height + padding
                      },
                    },
                  }}
                  sx={selectSx}
                >
                  <MenuItem value="">
                    <Typography component="span" sx={placeholderSx}>Select an object</Typography>
                  </MenuItem>

                  {availableObjects.map(object => (
                    <MenuItem key={object.id} value={object.id}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <TableChartOutlined fontSize="small" sx={{ color: 'primary.main' }} />
                        {object.name}
                        {object.type === 'custom' && (
                          <Chip
                            label="Custom"
                            size="small"
                            sx={[{ ml: 1, height: '22px' }, statusChipSx('info')]}
                          />
                        )}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
                {getFieldError('object') && (
                  <FormHelperText>{getFieldError('object')}</FormHelperText>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl
                fullWidth
                error={!!getFieldError('trigger')}
                disabled={!state.wizardData.object}
              >
                <InputLabel>Trigger Event *</InputLabel>
                <Select
                  value={state.wizardData.trigger || ''}
                  onChange={(e) => handleFieldChange('trigger', e.target.value)}
                  label="Trigger Event *"
                  disabled={!state.wizardData.object}
                  sx={selectSx}
                >
                  <MenuItem value="">
                    <Typography component="span" sx={placeholderSx}>Select a trigger</Typography>
                  </MenuItem>
                  {availableTriggers.map(trigger => (
                    <MenuItem key={trigger.id} value={trigger.id}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <BoltOutlined fontSize="small" sx={{ color: 'primary.main' }} />
                        {trigger.name}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
                {(getFieldError('trigger') || !state.wizardData.object) && (
                  <FormHelperText>
                    {getFieldError('trigger') || 'Choose an object first'}
                  </FormHelperText>
                )}
              </FormControl>
            </Grid>
          </Grid>
        </SectionCard>
      </Grid>

      <Grid item xs={12}>
        <Box
          sx={[
            {
              px: 2.5,
              py: 1.5,
              borderRadius: '12px',
              border: '1px solid',
              borderColor: 'outline.variant',
              bgcolor: 'surface.main'
            },
            (theme) => (state.wizardData.isActive ? tintSx(theme, 0.06) : {})
          ]}
        >
          <FormControlLabel
            control={
              <Switch
                checked={state.wizardData.isActive || false}
                onChange={(e) => handleFieldChange('isActive', e.target.checked)}
                color="info"
              />
            }
            label={
              <Box display="flex" alignItems="center" gap={1}>
                <PowerSettingsNewOutlined
                  sx={{
                    color: state.wizardData.isActive ? 'primary.main' : 'text.secondary',
                    fontSize: 20
                  }}
                />
                <Box>
                  <Typography sx={{ fontSize: FONT.body, fontWeight: 600, color: 'text.primary' }}>
                    Activate immediately
                  </Typography>
                  <Typography sx={{ fontSize: FONT.caption, color: 'text.secondary' }}>
                    {state.wizardData.isActive
                      ? 'The automation starts running as soon as it is saved.'
                      : 'The automation is saved as inactive. You can turn it on later from the list.'}
                  </Typography>
                </Box>
              </Box>
            }
          />
        </Box>
      </Grid>
    </Grid>
  );
}

TriggerStep.propTypes = {
  extraTriggers: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.string, name: PropTypes.string })),
};

TriggerStep.defaultProps = { extraTriggers: undefined };

export default TriggerStep;
